import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useHistory } from 'react-router-dom'
import { useAuth } from 'lib/auth'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers'
import { DateTime } from 'luxon'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import { draftToMarkdown } from 'markdown-draft-js'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import { dollarsToCents } from 'lib/utils/number'
import WizardForm from 'components/wizard-form'
import GetStartedStep from './steps/get-started'
import GetStartedRedesignStep from './steps/get-started-redesign'
import EventNameStep from './steps/event-name'
import ChooseTagsStep from './steps/choose-tags'
import SelectLocationStep from './steps/select-location'
import MultiSessionEventDayTimeStep from './steps/multi-session-event-day-time'
import MultiSessionEventDayTimeRedesignStep from './steps/multi-session-event-day-time-redesign'
import ExternalRegistrationUrlStep from './steps/external-registration-url'
import ExternalRegistrationUrlRedesignStep from './steps/external-registration-url-redesign'
import {
  RegistrationCloseDateStep,
  registrationCloseDates,
} from './steps/registration-close'
import MaxAttendanceStep from './steps/max-attendance'
import PaymentAccountStep from './steps/payment-account'
import PaymentAccountRedesignStep from './steps/payment-account-redesign'
import EventCostStep from './steps/event-cost'
import FeaturedImageStep from './steps/featured-image'
import FeaturedImageRedesignStep from './steps/featured-image-redesign'
import EventDescriptionStep from './steps/event-description'
import EventDetailsStep from './steps/event-details-redesign'
import SuccessStep from './steps/success'
import { gtmEvent } from 'lib/gtm'
import UnlistedStep from './steps/unlisted'
import UnlistedRedesignStep from './steps/unlisted-redesign'
import AttendanceRestrictions from './steps/attendance-restrictions'
import AttendanceRestrictionsRedesignStep from './steps/attendance-restrictions-redesign'
import { apolloClient } from 'lib/graphql'
import { CREATE_GROUP_EVENT, COACH_SCHEDULE_AVAILABILITY } from './queries'
import { inputPriceRegExp } from 'utils/regExp'

const defaultValues = {
  startDate: null,
  startTime: null,
  title: '',
  endTime: null,
  tags: [''],
  featuredImageUrl: '',
  isExternalRegistration: false,
  externalRegistrationUrl: null,
  paymentRecurrenceSchedule: undefined,
  unlisted: false,
  locationId: null,
  academyId: null,
  hoursBeforeRegistrationCloses: registrationCloseDates[0].hoursBefore,
  sessions: (() => {
    const startDateTime = DateTime.local().plus({ weeks: 1 })
    return [
      {
        startDate: startDateTime.startOf('day'),
        startTime: startDateTime.set({ hour: 9, minute: 0 }),
        endTime: startDateTime.set({ hour: 10, minute: 0 }),
      },
    ]
  })(),
}

const verifySession = async (
  value,
  user,
  locationId,
  coachFacilities,
  coachAcademies,
  coachFacilityLocations,
) => {
  const { startTime, startDate, endTime } = value

  const selectedFacility = coachFacilityLocations.find(
    facility => facility.id === locationId,
  )
  const { data } = await apolloClient.query({
    query: COACH_SCHEDULE_AVAILABILITY,
    variables: {
      id: user?.coach?.id,
      timespan: {
        from: buildDateTime(startDate, startTime, selectedFacility.timezone),
        to: buildDateTime(startDate, endTime, selectedFacility.timezone),
      },
    },
    fetchPolicy: 'cache-first',
  })
  return data?.coach?.scheduleAvailability
}

const validationSchema = (
  coachFacilities,
  paymentAccounts,
  coachAcademies,
  coachFacilityLocations,
  isGroupEventAccessCodeEnabled,
  isEventCreationRedesignEnabled,
  isRecurringPaymentScheduleEnabled,
  user,
) => {
  return yup.object().shape({
    title: yup
      .string()
      .min(1)
      .required(),
    maxAttendance: yup
      .number()
      .nullable()
      .transform((value, originalValue) => {
        return String(originalValue).trim() === '' ? null : value
      }),
    price: yup
      .string()
      .matches(inputPriceRegExp, 'Please enter a valid price')
      .required('Please specify a price per person'),
    unlisted: yup.boolean().required(),
    ...(isEventCreationRedesignEnabled
      ? {
        paymentRecurrenceSchedule: yup.string().when('isExternalRegistration', {
          is: true,
          then: yup
            .string()
            .oneOf(['none'], 'External events must collect payments maually')
            .required('Please select a payment schedule'),
          otherwise: yup
            .string()
            .oneOf(
              isRecurringPaymentScheduleEnabled
                ? ['once', 'week', 'month', 'none']
                : ['once', 'none'],
              'Please select a payment schedule',
            )
            .required('Please select a payment schedule'),
        }),
        paymentAccountId: yup
          .string()
          .oneOf(['', ...paymentAccounts.map(pa => pa.id)])
          .when('paymentRecurrenceSchedule', paymentRecurrenceSchedule => {
            if (paymentRecurrenceSchedule !== 'none') {
              return yup.string().required('Please select a payment account')
            } else {
              return yup.string().nullable()
            }
          }),
      }
      : {
        paymentAccountId: yup
          .string()
          .oneOf(['', ...paymentAccounts.map(pa => pa.id)])
          .nullable(),
      }),
    locationId: yup.string().required(),
    tags: yup
      .array()
      .required()
      .min(1)
      .max(5),
    featuredImageUrl: yup.string().required(),
    sessions: yup
      .array()
      .min(1, 'Group events must have at least one date/time occurrence')
      .required()
      .when('locationId', (locationId, schema) => {
        return schema.of(
          yup
            .object()
            .shape({
              startDate: yup.date().required(),
              startTime: yup
                .date()
                .typeError('Start time must be a valid time')
                .label('Start Time')
                .required(),
              endTime: yup
                .date()
                .typeError('End time must be a valid time')
                .label('End Time')
                .when('startTime', (startTime, schema) => {
                  const refStartTime = new Date(startTime)
                  refStartTime.setSeconds(1)
                  return schema.min(
                    refStartTime,
                    'End time must be after start time',
                  )
                })
                .required(),
            })
            .test(
              'is correct session',
              'session validation error',
              async function (value) {
                const scheduleAvailability = await verifySession(
                  value,
                  user,
                  locationId,
                  coachFacilities,
                  coachAcademies,
                  coachFacilityLocations,
                )
                if (
                  scheduleAvailability?.isAvailable &&
                  scheduleAvailability?.warnings.length === 0
                ) {
                  return true
                } else {
                  const errorMessage =
                    scheduleAvailability?.warnings[0] ||
                    'session validation error'
                  return this.createError({ message: errorMessage })
                }
              },
            ),
        )
      }),
    isExternalRegistration: yup.boolean(),
    externalRegistrationUrl: yup
      .string()
      .url('Please enter a valid URL')
      .when('isExternalRegistration', {
        is: true,
        then: yup.string().required('Please enter a valid URL'),
      }),
    hoursBeforeRegistrationCloses: yup.number().when('isExternalRegistration', {
      is: false,
      then: yup.number().required(),
    }),
    ...(isGroupEventAccessCodeEnabled && {
      accessCode: yup.string().matches(/^[a-z0-9]+$/i, {
        message: 'Access codes must consist of numbers and letters only',
        excludeEmptyString: true.valueOf,
      }),
    }),
  })
}

const CreateGroupEventFormWizard = ({
  coachFacilities,
  paymentAccounts,
  coachAcademies = [],
  coachFacilityLocations,
}) => {
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const [createdGroupEvent, setCreatedGroupEvent] = useState(null)
  const [
    isExternalRegistrationEnabled,
    isGroupEventAccessCodeEnabled,
    isEventCreationRedesignEnabled,
    isRecurringPaymentScheduleEnabled,
  ] = useFeatureFlags([
    flags.FLAG_FEAT_EXTERNAL_REGISTRATION,
    flags.FLAG_FEAT_GROUP_EVENT_ACCESS_CODES,
    flags.FLAG_FEAT_EVENT_CREATION_REDESIGN,
    flags.FLAG_FEAT_GROUP_EVENT_RECURRING_PAYMENTS_ENABLED,
  ])
  const { user } = useAuth()
  const [createGroupEvent] = useMutation(CREATE_GROUP_EVENT, {
    onCompleted: data => {
      if (data.createGroupEvent.success) {
        if (user.onboarding) {
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'group-event',
            formAction: 'completed-onboarding-create-group-event',
          })
        } else {
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'group-event',
            formAction: 'completed-create-group-event',
          })
        }
        setCreatedGroupEvent(data.createGroupEvent.groupEvent)
      } else {
        enqueueSnackbar(data.createGroupEvent.message, {
          variant: 'error',
        })
      }
    },
  })
  if (createdGroupEvent) {
    return (
      <SuccessStep
        slug={createdGroupEvent.slug}
        onCreateAnother={() => setCreatedGroupEvent(null)}
      />
    )
  }

  const buildEventCreationSteps = formValues => [
    {
      component: <GetStartedStep />,
      fieldsToValidate: [],
      nextButtonLabel: 'Start from scratch',
      stepName: 'get-started',
    },
    {
      component: <EventNameStep />,
      fieldsToValidate: ['title'],
      nextButtonLabel: 'Next: Tags',
      stepName: 'event-name',
    },
    {
      component: <ChooseTagsStep />,
      fieldsToValidate: ['tags'],
      nextButtonLabel: 'Next: Location',
      stepName: 'choose-tags',
    },
    {
      component: <SelectLocationStep />,
      fieldsToValidate: ['locationId'],
      nextButtonLabel: 'Next: Event Date',
      stepName: 'select-location',
    },
    {
      component: <MultiSessionEventDayTimeStep facilities={coachFacilities} />,
      fieldsToValidate: ['sessions'],
      nextButtonLabel: isExternalRegistrationEnabled
        ? 'Next: External Registration'
        : 'Next: Registration Close Date',
      stepName: 'event-day-time',
    },
    ...(isExternalRegistrationEnabled
      ? [
        {
          component: <ExternalRegistrationUrlStep />,
          fieldsToValidate: ['externalRegistrationUrl'],
          nextButtonLabel: !formValues.isExternalRegistration
            ? 'Next: Registration Close Date'
            : 'Next: Price',
          stepName: 'external-registration-url',
        },
      ]
      : []),
    ...(!formValues.isExternalRegistration
      ? [
        {
          component: <RegistrationCloseDateStep />,
          fieldsToValidate: ['hoursBeforeRegistrationCloses'],
          nextButtonLabel: isGroupEventAccessCodeEnabled
            ? 'Next: Attendance Restrictions'
            : 'Next: Max Attendance',
          stepName: 'registration-close',
        },
        ...(isGroupEventAccessCodeEnabled
          ? [
            {
              component: <AttendanceRestrictions />,
              fieldsToValidate: ['maxAttendance', 'accessCode'],
              nextButtonLabel: 'Next: Payment Collection Method',
              stepName: 'attendance-restrictions',
            },
          ]
          : [
            {
              component: <MaxAttendanceStep />,
              fieldsToValidate: ['maxAttendance'],
              nextButtonLabel: 'Next: Payment Collection Method',
              stepName: 'max-attendance',
            },
          ]),
        {
          component: <PaymentAccountStep />,
          fieldsToValidate: ['paymentAccountId'],
          nextButtonLabel: 'Next: Price',
          stepName: 'event-payment-account',
        },
      ]
      : []),
    {
      component: <EventCostStep />,
      fieldsToValidate: ['price'],
      nextButtonLabel: 'Next: Featured Image',
      stepName: 'event-cost',
    },
    {
      component: <FeaturedImageStep />,
      fieldsToValidate: ['featuredImageUrl'],
      nextButtonLabel: 'Next: Description',
      stepName: 'featured-image',
    },
    {
      component: <EventDescriptionStep />,
      fieldsToValidate: ['description'],
      nextButtonLabel: !formValues.isExternalRegistration
        ? 'Next: Visibility'
        : 'Create Event',
      stepName: 'event-description',
    },
    ...(!formValues.isExternalRegistration && [
      {
        component: <UnlistedStep />,
        fieldsToValidate: ['unlisted'],
        nextButtonLabel: 'Create Event',
        stepName: 'unlisted',
      },
    ]),
  ]

  const buildRedesignedEventCreationSteps = formValues => [
    {
      component: <GetStartedRedesignStep />,
      fieldsToValidate: [],
      nextButtonLabel: 'Start from scratch',
      stepName: 'get-started',
    },
    {
      component: <EventDetailsStep />,
      fieldsToValidate: ['title', 'locationId', 'tags', 'description'],
      nextButtonLabel: 'Next: Featured Image',
      stepName: 'event-details',
    },
    {
      component: <FeaturedImageRedesignStep />,
      fieldsToValidate: ['featuredImageUrl'],
      nextButtonLabel: 'Next: Recurrence',
      stepName: 'featured-image',
    },
    {
      component: (
        <MultiSessionEventDayTimeRedesignStep facilities={coachFacilities} />
      ),
      fieldsToValidate: ['sessions'],
      nextButtonLabel: 'Next: Registration',
      stepName: 'event-day-time',
    },
    {
      component: <ExternalRegistrationUrlRedesignStep />,
      fieldsToValidate: formValues.isExternalRegistration
        ? ['externalRegistrationUrl']
        : ['hoursBeforeRegistrationCloses'],
      nextButtonLabel: formValues.isExternalRegistration
        ? 'Next: Price'
        : 'Next: Restrictions',
      stepName: 'external-registration-url',
    },
    ...(!formValues.isExternalRegistration
      ? [
        {
          component: <AttendanceRestrictionsRedesignStep />,
          fieldsToValidate: ['maxAttendance', 'accessCode'],
          nextButtonLabel: 'Next: Price',
          stepName: 'attendance-restrictions',
        },
      ]
      : []),
    {
      component: <PaymentAccountRedesignStep />,
      fieldsToValidate: [
        'paymentRecurrenceSchedule',
        'price',
        'paymentAccountId',
      ],
      nextButtonLabel: !formValues.isExternalRegistration
        ? 'Next: Visibility'
        : 'Create Event',
      stepName: 'event-cost',
    },
    ...(!formValues.isExternalRegistration
      ? [
        {
          component: <UnlistedRedesignStep />,
          fieldsToValidate: ['unlisted'],
          nextButtonLabel: 'Create Event',
          stepName: 'unlisted',
        },
      ]
      : []),
  ]

  return (
    <WizardForm
      formProps={{
        defaultValues: {
          ...defaultValues,
          locationId: coachFacilities[0]?.id || null,
        },
        resolver: yupResolver(
          validationSchema(
            coachFacilities,
            paymentAccounts,
            coachAcademies,
            coachFacilityLocations,
            isGroupEventAccessCodeEnabled,
            isEventCreationRedesignEnabled,
            isRecurringPaymentScheduleEnabled,
            user,
          ),
        ),
      }}
      formName="group-event"
      steps={
        isEventCreationRedesignEnabled
          ? buildRedesignedEventCreationSteps
          : buildEventCreationSteps
      }
      extraData={{
        coachFacilities,
        paymentAccounts,
        coachAcademies,
      }}
      onSubmit={async formValues => {
        const selectedFacility = coachFacilityLocations?.find(
          cf => cf.id === formValues.locationId,
        )
        const startDate = formValues.sessions[0].startDate
        const startTime = formValues.sessions[0].startTime
        const startDateTime = buildDateTime(
          startDate,
          startTime,
          selectedFacility.timezone,
        )
        return createGroupEvent({
          variables: {
            groupEvent: {
              title: formValues.title,
              academyId: formValues.academyId,
              description: formValues.description
                ? draftToMarkdown(JSON.parse(formValues.description))
                : null,
              registrationCloseDateTime: !formValues.isExternalRegistration
                ? startDateTime.minus({
                  hours: formValues.hoursBeforeRegistrationCloses,
                })
                : null,
              tags: formValues.tags,
              maxAttendance: formValues.maxAttendance
                ? parseInt(formValues.maxAttendance, 10)
                : null,
              unlisted: formValues.unlisted,
              priceInCents: dollarsToCents(formValues.price),
              externalRegistrationUrl: formValues.isExternalRegistration
                ? formValues.externalRegistrationUrl
                : null,
              featuredImageUrl: formValues.featuredImageUrl,
              ...(isEventCreationRedesignEnabled
                ? {
                  paymentRecurrenceSchedule:
                    formValues.paymentRecurrenceSchedule,
                  paymentAccountId:
                    formValues.paymentRecurrenceSchedule !== 'none' &&
                    (formValues.paymentAccountId || '').length > 0
                      ? formValues.paymentAccountId
                      : null,
                }
                : {
                  paymentAccountId:
                    (formValues.paymentAccountId || '').length > 0
                      ? formValues.paymentAccountId
                      : null,
                }),
              ...(isGroupEventAccessCodeEnabled &&
                formValues.accessCode && {
                  accessCode:
                    (formValues.accessCode || '').length > 0
                      ? formValues.accessCode
                      : null,
                }),
              sessions: formValues.sessions.map(session => ({
                facilityId: selectedFacility.id,
                startDateTime: buildDateTime(
                  session.startDate,
                  session.startTime,
                  selectedFacility.timezone,
                ),
                endDateTime: buildDateTime(
                  session.startDate,
                  session.endTime,
                  selectedFacility.timezone,
                ),
              })),
            },
          },
        })
      }}
      onCancel={() => history.push('/pga-coach/bookings/events')}
    />
  )
}

export default CreateGroupEventFormWizard
